/**
 * Pure helpers for the house rules editor: grouping rules by (stage, seat),
 * parsing what the admin typed into patterns, working out where a stage with
 * no rules of its own gets them from, and summarising the pool's candidates.
 * Kept out of the component so CRA's jest can test them without the router.
 */
import type {
  BenchAdvice,
  BenchStandout,
  PoolCandidates,
  StageEnumOption,
  StageModelResult,
  StageModelRule,
} from '../types';

export const ruleKey = (stage: string, archetype: string | null | undefined): string =>
  `${stage}|${archetype ?? ''}`;

/** Rules by (stage, seat), in the order the server returned them. */
export function groupRules(rules: StageModelRule[]): Map<string, StageModelRule[]> {
  const out = new Map<string, StageModelRule[]>();
  for (const r of rules) {
    const k = ruleKey(r.stage, r.archetype);
    const list = out.get(k);
    if (list) list.push(r);
    else out.set(k, [r]);
  }
  return out;
}

/** "gemma4:*, Claude-Opus-4-8 deepseek-v4" → ["gemma4:*", "claude-opus-4-8", "deepseek-v4"]. */
export function parsePatterns(input: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of input.split(/[\s,]+/)) {
    const p = raw.trim().toLowerCase();
    if (!p || seen.has(p)) continue;
    seen.add(p);
    out.push(p);
  }
  return out;
}

export interface StageRules {
  /** The rung that answers for this stage: itself, its group, DEFAULT, or none. */
  from: string | null;
  fromLabel: string | null;
  own: boolean;
  rules: StageModelRule[];
}

/** Which rules a stage runs on, walking its fallback chain over the seat-less
 * rules: its own, then its group's, then DEFAULT's — the resolver's order,
 * taken from the stage option's `fallback` rather than re-derived. */
export function rulesFor(stage: StageEnumOption, grouped: Map<string, StageModelRule[]>): StageRules {
  const enabledOf = (key: string) => (grouped.get(key) ?? []).filter((r) => r.enabled);
  const own = enabledOf(ruleKey(stage.value, null));
  if (own.length) return { from: stage.value, fromLabel: stage.label, own: true, rules: own };
  for (const rung of stage.fallback ?? []) {
    const rules = enabledOf(ruleKey(rung.stage, null));
    if (rules.length) return { from: rung.stage, fromLabel: rung.label, own: false, rules };
  }
  return { from: null, fromLabel: null, own: false, rules: [] };
}

/** DEFAULT first, then group stages, then the rest by label — the order an
 * admin reads the mapping in, most-shared at the top. */
export function orderStages(stages: StageEnumOption[]): StageEnumOption[] {
  const rank = (s: StageEnumOption) => (s.value === 'DEFAULT' ? 0 : s.is_group ? 1 : 2);
  return [...stages].sort((a, b) => rank(a) - rank(b) || a.label.localeCompare(b.label));
}

/** Apps in the order the editor lists them. Shared first: those are the rungs
 * every other job inherits from, so they read as the floor rather than as a
 * fifth product. */
export const APP_ORDER = ['', 'PUBLISH', 'BOARDROOM', 'BEATS'] as const;

export const APP_LABEL: Record<string, string> = {
  '': 'Shared - every app',
  PUBLISH: 'Publish',
  BOARDROOM: 'Recon',
  BEATS: 'News Beats',
};

export interface AppGroup {
  product: string; // '' = shared
  label: string;
  stages: StageEnumOption[];
}

/** What a collapsed app is hiding: how many of its jobs set their own models
 * rather than following a shared rule. The count that decides whether it is
 * worth opening. */
export function appSummary(
  stages: StageEnumOption[],
  grouped: Map<string, StageModelRule[]>,
): string {
  const own = stages.filter((s) =>
    (grouped.get(ruleKey(s.value, null)) ?? []).some((r) => r.enabled),
  ).length;
  const n = stages.length;
  const jobs = `${n} job${n === 1 ? '' : 's'}`;
  const base =
    own === 0
      ? `${jobs}, all following shared rules`
      : own === n
        ? `${jobs}, each with its own models`
        : `${jobs}, ${own} with their own models`;
  // A folded app has to say when something in it cannot run: that is the one
  // thing worth unfolding it for.
  const broken = stages.filter((s) => cannotRun(s, grouped) !== null).length;
  return broken ? `${base} · ${broken} can't run` : base;
}

/** Why a job cannot run on these rules, or null when it can. Judged on the
 *  rules' NAMES (`matches` is the house model names right now), which is what
 *  catches a rule left pointing at a deleted model — seen live: a draft's
 *  DEFAULT allowed only `gemma4:31b-cloud` a day after that model was removed,
 *  and every boardroom turn failed while the rule still looked set. A model on
 *  a paused provider is the pool preview's to explain. */
export function cannotRun(stage: StageEnumOption, grouped: Map<string, StageModelRule[]>): string | null {
  const eff = rulesFor(stage, grouped);
  if (!eff.from) return 'nothing allowed - no rule here, on its group, or on DEFAULT';
  return deadRulesReason(eff.rules, eff.own ? null : eff.fromLabel);
}

/** "gemma4:31b-cloud matches no model" when every enabled rule in `rules`
 *  matches nothing; null when one of them matches, or there are none. */
export function deadRulesReason(rules: StageModelRule[], fromLabel: string | null = null): string | null {
  const enabled = rules.filter((r) => r.enabled);
  if (!enabled.length || enabled.some((r) => r.matches.length)) return null;
  const pats = enabled.map((r) => r.model_pattern).join(', ');
  const verb = enabled.length === 1 ? 'matches' : 'match';
  return `can't run: ${pats}${fromLabel ? ` (from ${fromLabel})` : ''} ${verb} no model`;
}

/** Seats of a job whose own rules all match nothing. A seat with rules of its
 *  own does not fall back to the every-seat rules, so these seats cannot run
 *  whatever the job's rules say. */
export function seatsThatCannotRun(stageValue: string, rules: StageModelRule[]): string[] {
  const bySeat = new Map<string, StageModelRule[]>();
  for (const r of rules) {
    if (r.stage !== stageValue || !r.archetype) continue;
    const list = bySeat.get(r.archetype);
    if (list) list.push(r);
    else bySeat.set(r.archetype, [r]);
  }
  return Array.from(bySeat.entries())
    .filter(([, rs]) => deadRulesReason(rs) !== null)
    .map(([seat]) => seat)
    .sort();
}

/** The jobs split by the app they belong to, each already in reading order.
 * Empty apps are dropped, so a customer holding one product sees two headings
 * rather than five. */
export function groupStagesByApp(stages: StageEnumOption[]): AppGroup[] {
  const byApp = new Map<string, StageEnumOption[]>();
  for (const s of orderStages(stages)) {
    const key = s.product ?? '';
    const list = byApp.get(key);
    if (list) list.push(s);
    else byApp.set(key, [s]);
  }
  const known = APP_ORDER.filter((k) => byApp.has(k)) as string[];
  // An app the frontend has never heard of still gets a heading rather than
  // vanishing — a fifth product must not silently hide its jobs.
  const rest = Array.from(byApp.keys())
    .filter((k) => !known.includes(k))
    .sort();
  return [...known, ...rest].map((product) => ({
    product,
    label: APP_LABEL[product] ?? product,
    stages: byApp.get(product)!,
  }));
}

/** "3 candidates on 2 providers", or the pool's reason there are none. */
export function candidateSummary(c: PoolCandidates | null): string {
  if (!c) return '';
  if (!c.candidates.length) return c.reason ?? 'No candidates';
  const providers = new Set(c.candidates.map((x) => x.provider_id)).size;
  const n = c.candidates.length;
  return `${n} candidate${n === 1 ? '' : 's'} on ${providers} provider${providers === 1 ? '' : 's'}`;
}

const GLOB_CHARS = /[*?[]/;

/** A rule pattern as the backend reads it: exact name or glob, case-insensitive. */
export function patternMatches(pattern: string, name: string): boolean {
  const p = pattern.trim().toLowerCase();
  const n = name.trim().toLowerCase();
  if (!p || !n) return false;
  if (!GLOB_CHARS.test(p)) return p === n;
  const re = new RegExp(
    '^' + p.replace(/[.+^${}()|\\]/g, '\\$&').replace(/\*/g, '.*').replace(/\?/g, '.') + '$',
  );
  return re.test(n);
}

/** What the admin is LOOKING FOR as they type: a glob matches as a glob; plain
 * text narrows by substring, so "glm" shows every glm model before they decide
 * whether to allow one name or `*glm*`. Empty shows everything. */
export function narrow<T extends { model_name: string }>(models: T[], input: string): T[] {
  const p = input.trim().toLowerCase();
  if (!p) return models;
  if (GLOB_CHARS.test(p)) return models.filter((m) => patternMatches(p, m.model_name));
  return models.filter((m) => m.model_name.toLowerCase().includes(p));
}

/** The model names a pattern would allow, for the preview under a pattern being
 *  edited: "gemma4:31*" has to show that it takes in gemma4:31b AND
 *  gemma4:31b-cloud before it is worth saving. Unlike `narrow`, plain text here
 *  is a pattern, not a search: it matches by name, not by substring. */
export function matchingNames<T extends { model_name: string }>(pattern: string, models: T[]): string[] {
  if (!pattern.trim()) return [];
  return models
    .filter((m) => patternMatches(pattern, m.model_name))
    .map((m) => m.model_name)
    .sort();
}

/** The glob to offer for plain text that is not itself a model name. */
export function suggestedGlob(input: string): string | null {
  const p = input.trim().toLowerCase();
  if (!p || GLOB_CHARS.test(p)) return null;
  return `*${p}*`;
}

/** The stage bench's suggestion as one sentence for the top of the job's modal. */
export function adviceHeadline(advice: BenchAdvice | null): string | null {
  if (!advice || advice.status === 'no_data') return null;
  const pick = advice.pick ?? '';
  switch (advice.status) {
    case 'nothing_clean':
      return 'No benched model did this job cleanly - keep what works and bench more models.';
    case 'held':
      return `Leaning ${pick}, but the tie is too close to act on: ${advice.held_back}.`;
    case 'switch':
      return `The bench suggests ${pick} - it is not allowed here yet.`;
    case 'mixed':
      return `${pick} is allowed, but the pool also rotates onto ${advice.worse_allowed.join(', ')}, which measured worse.`;
    case 'on_pick':
      return `On the bench's pick (${pick}).`;
    default:
      return null;
  }
}

/** A metric value the way a person reads it: rates as percents, harm per run,
 * token counts in thousands, seconds with one decimal. */
export function formatMetric(name: string, value: number | undefined): string {
  if (value === undefined || value === null || Number.isNaN(value)) return '-';
  switch (name) {
    case 'harm':
      return value === 0 ? '0' : value.toFixed(2);
    case 'tokens':
    case 'output_tokens':
      return value >= 10_000 ? `${Math.round(value / 1000)}k` : String(Math.round(value));
    case 'seconds':
      return `${value.toFixed(1)}s`;
    case 'calls':
      return String(Math.round(value * 10) / 10);
    default:
      return `${Math.round(value * 100)}%`;
  }
}

/** Standouts by model: "gemma4:31b" → ["Tokens", "Speed"]. The overall pick is
 * a verdict on clean runs; this is where every OTHER model did best. */
export function standoutsByModel(standouts: BenchStandout[]): Map<string, string[]> {
  const out = new Map<string, string[]>();
  for (const s of standouts) {
    for (const model of s.leaders) {
      const list = out.get(model);
      if (list) list.push(s.label);
      else out.set(model, [s.label]);
    }
  }
  return out;
}

/** The bench panel's plain-language summary under the headline: who is tied
 * for best, the fastest and cheapest models that still do the job, and the
 * ones too slow for someone waiting. Each line only when it says something. */
export function adviceSummary(advice: BenchAdvice): string[] {
  const rows = advice.rows;
  if (!rows.length) return [];
  const out: string[] = [];
  const score = (r: BenchAdvice['rows'][number]) => `${r.clean_runs}/${r.n}`;
  const tied = rows.filter((r) => r.tied);
  if (tied.length > 1) {
    out.push(`Tied for best: ${tied.map((r) => `${r.model} (${score(r)})`).join(', ')}.`);
  }
  const best = Math.max(...rows.map((r) => (r.n ? r.clean_runs / r.n : 0)));
  const doesTheJob = rows.filter((r) => r.n && r.clean_runs / r.n >= best / 2);
  const pick = rows.find((r) => r.pick);
  const fastest = [...doesTheJob].sort((a, b) => a.seconds - b.seconds)[0];
  if (fastest && fastest !== pick) {
    out.push(`Fastest that still does the job: ${fastest.model} (${fastest.seconds.toFixed(0)}s, ${score(fastest)}).`);
  }
  const cheapest = [...doesTheJob].sort((a, b) => a.tokens - b.tokens)[0];
  if (cheapest && cheapest !== pick) {
    out.push(`Cheapest that still does the job: ${cheapest.model} (${formatMetric('tokens', cheapest.tokens)} tokens, ${score(cheapest)}).`);
  }
  const slow = rows.filter((r) => r.metrics.on_time !== undefined && r.metrics.on_time < 0.5);
  if (slow.length) {
    out.push(`Too slow for someone waiting (on time under half the runs): ${slow.map((r) => r.model).join(', ')}.`);
  }
  return out;
}

/** Which metric columns earn space. The main table always shows On time
 * (when the job has a budget), Speed, Tokens and Harm; every other metric goes
 * behind "More metrics", and one where every model is at 95% or better is
 * named once instead of drawn as a column of green. */
export function splitAdviceColumns(advice: BenchAdvice): {
  main: string[];
  extra: string[];
  saturated: string[];
} {
  const MAIN = ['on_time', 'seconds', 'tokens', 'harm'];
  const present = new Set(advice.columns);
  const main = MAIN.filter((c) => present.has(c));
  const extra: string[] = [];
  const saturated: string[] = [];
  for (const name of advice.columns) {
    if (name === 'clean_rate' || MAIN.includes(name)) continue;
    const def = advice.metric_defs.find((d) => d.name === name);
    const values = advice.rows.map((r) => r.metrics[name]).filter((v) => v !== undefined);
    if (def?.kind === 'quality' && values.length && values.every((v) => v >= 0.95)) {
      saturated.push(def.label);
    } else {
      extra.push(name);
    }
  }
  return { main, extra, saturated };
}

/** Where a recorded result came from, in words: the stage bench (with its
 * counts), the old brainstorm seat bench (imported, no counts, prompts that no
 * longer exist), by hand, or a script. */
export function resultSource(r: StageModelResult): string {
  const detail = r.detail as { clean_runs?: number } | null | undefined;
  if (r.source === 'bench') {
    return detail && typeof detail.clean_runs === 'number' ? 'bench' : 'imported seat bench';
  }
  return r.source === 'manual' ? 'by hand' : r.source === 'ad_hoc' ? 'script' : r.source;
}

/** The providers a bench result ran on, from its recorded detail. */
export function resultProviders(r: StageModelResult): string[] {
  const providers = (r.detail as { providers?: unknown } | null | undefined)?.providers;
  return Array.isArray(providers) ? providers.filter((p): p is string => typeof p === 'string') : [];
}

/** What deleting a result does, when it is more than tidying: a stage-bench
 * row is what the suggestion at the top reads, so removing it takes that model
 * out of the suggestion until the bench records it again. Null = no warning. */
export function deleteResultWarning(r: StageModelResult): string | null {
  if (resultSource(r) !== 'bench') return null;
  return (
    `Delete the bench result for ${r.model_name}? It drops out of the suggestion above ` +
    'until the bench records this job again (main-bench.py run or record).'
  );
}

export type RowRuleAction =
  | { kind: 'allow' }
  /** Allowed today, but not by a rule naming it: another job's rule, or a glob.
   *  Pinning writes the exact name here, so the model that was benched and
   *  approved keeps running when that other rule changes. `from` is what allows
   *  it today. */
  | { kind: 'pin'; from: string | null }
  | { kind: 'remove'; ruleIds: number[]; lastRule: boolean };

/** What the Allow / Remove button on a recommended model does, against this
 * job's OWN rules (`here`, already narrowed to the seat):
 *  - not allowed: add an exact rule for it;
 *  - allowed by an exact rule here: remove that rule (flagging when it is the
 *    job's last, since the job then falls back to its group or DEFAULT);
 *  - allowed by a glob here, or by inheritance: PIN it — write the exact name
 *    here. Never remove in those cases: a glob carries other models, and an
 *    inherited rule belongs to another job. The bench's pick reading "allowed ·
 *    inherited" with nothing to click was the dead end — the job had no value
 *    of its own, so changing DEFAULT (or a model that stopped matching the
 *    glob) silently took away what was benched and approved. */
export function rowRuleAction(
  model: string,
  allowed: boolean,
  here: StageModelRule[],
  inheritedFrom: string | null = null,
): RowRuleAction {
  if (!allowed) return { kind: 'allow' };
  const name = model.trim().toLowerCase();
  const enabled = here.filter((r) => r.enabled);
  const exact = here.filter((r) => r.model_pattern.trim().toLowerCase() === name);
  if (exact.length) {
    const remaining = enabled.filter((r) => !exact.includes(r));
    return { kind: 'remove', ruleIds: exact.map((r) => r.id), lastRule: remaining.length === 0 };
  }
  const glob = enabled.find((r) => patternMatches(r.model_pattern, model));
  return { kind: 'pin', from: glob ? glob.model_pattern : inheritedFrom };
}

/** "Use only the pick": the rules to remove from this job (and seat) — every
 * rule except one naming the pick exactly. Removing disabled rules too, so a
 * later Enable cannot quietly bring a worse model back. */
export function rulesReplacedByPick(here: StageModelRule[], pick: string): StageModelRule[] {
  const p = pick.trim().toLowerCase();
  return here.filter((r) => r.model_pattern.trim().toLowerCase() !== p);
}

/** The names a pattern matches, as a short label: "gemma4:31b, gemma4:latest" or "no match". */
export function matchesLabel(rule: StageModelRule): string {
  if (!rule.matches.length) return 'matches nothing yet';
  if (rule.matches.length === 1 && rule.matches[0] === rule.model_pattern) return '';
  return rule.matches.join(', ');
}
